import { Alert, Button, Image, Input, Modal, Spin, Tooltip } from 'antd';
import {
	getMapTargets,
	postAdminPanelRemapStock,
	postAdminPanelRemapTransactions,
	postApi,
	postMapDraftRequest,
	postMedicineCatalogCsvSync,
} from 'apis';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useDebounce } from 'utils/hooks';
import { toastText } from 'utils/utils';
import type {
	DraftMedicineRequestRow,
	MapTargetRow,
	MedicineDetailsByIdsRow,
} from '../types';
import styles from './index.module.scss';

interface MapMedicineModalProps {
	open: boolean;
	draftRequest: DraftMedicineRequestRow | null;
	onClose: () => void;
	onMapped: () => void;
}

const getAxiosErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.error?.errorDescription ??
	error?.response?.data?.message ??
	error?.message ??
	fallback;

const pharmaciesFailedCount = (response: any): number => {
	const failed = response?.data?.data?.pharmaciesFailed;
	return typeof failed === 'number' ? failed : 0;
};

const medicineImageUrl = (url?: string | null) => {
	if (!url?.trim()) return null;
	if (/^https?:\/\//i.test(url)) return url;
	const base = process.env.REACT_APP_S3_BASE_URL || '';
	return `${base}${url}`;
};

const formatLabel = (value?: string | null) => {
	if (!value) return null;
	return value
		.toLowerCase()
		.split(/[_\s]+/)
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

const formatProductSize = (
	weightage?: number | string | null,
	unitOfMeasure?: string | null,
) => {
	const w =
		weightage != null && String(weightage).trim() !== ''
			? String(weightage).trim()
			: '';
	const unit = formatLabel(unitOfMeasure) || unitOfMeasure?.trim() || '';
	if (w && unit) return `${w} ${unit}`;
	if (w) return w;
	if (unit) return unit;
	return null;
};

const formatQuantity = (
	numberOfItems?: string | null,
	unitOfMeasure?: string | null,
) => {
	const count = numberOfItems?.trim();
	const unit = formatLabel(unitOfMeasure);
	if (count && unit) {
		const n = Number(count);
		const plural =
			!Number.isNaN(n) && n !== 1 && !unit.toLowerCase().endsWith('s')
				? `${unit}s`
				: unit;
		return `${count} ${plural}`;
	}
	if (count) return count;
	if (unit) return unit;
	return null;
};

const displayOrDash = (value?: string | null) => {
	const trimmed = value?.toString().trim();
	return trimmed ? trimmed : '—';
};

const formatMrp = (mrp?: number | string | null) => {
	if (mrp == null || mrp === '') return null;
	const n = Number(mrp);
	if (!Number.isFinite(n) || n <= 0) return null;
	return `₹${n.toFixed(2)}`;
};

type LabeledTag = {
	key: string;
	label: string;
	value: string;
	variant?: 'approved';
};

const getTargetLabeledTags = (target: MapTargetRow): LabeledTag[] => {
	const tags: LabeledTag[] = [];

	if (target.marketedBy?.trim()) {
		tags.push({
			key: 'marketedBy',
			label: 'Marketed By',
			value: target.marketedBy.trim(),
		});
	}
	const weightage = formatProductSize(
		target.weightage,
		target.unitOfMeasure,
	);
	if (weightage) {
		tags.push({ key: 'weightage', label: 'Weightage', value: weightage });
	}
	const unitOfMeasure =
		formatLabel(target.unitOfMeasure) || target.unitOfMeasure?.trim();
	if (unitOfMeasure) {
		tags.push({
			key: 'form',
			label: 'Form',
			value: unitOfMeasure,
		});
	}
	const category = target.category?.categoryName?.trim();
	if (category) {
		tags.push({ key: 'category', label: 'Category', value: category });
	}
	tags.push({
		key: 'mrp',
		label: 'MRP',
		value: formatMrp(target.mrp) || '—',
	});

	return tags;
};

const LabeledTagChip = ({ tag }: { tag: LabeledTag }) => {
	const chip = (
		<span
			className={`${styles.tag} ${tag.variant === 'approved' ? styles.tagApproved : ''}`}
		>
			<span className={styles.tagLabel}>{tag.label}:</span>{' '}
			<span className={styles.tagValue}>{tag.value}</span>
		</span>
	);

	return <Tooltip title={`${tag.label}: ${tag.value}`}>{chip}</Tooltip>;
};

const TruncatedMetaValue = ({ value }: { value?: string | null }) => {
	const text = value?.trim();
	if (!text) {
		return <span className={styles.metaValue}>—</span>;
	}

	return (
		<Tooltip title={text}>
			<span className={`${styles.metaValue} ${styles.metaValueTruncate}`}>
				{text}
			</span>
		</Tooltip>
	);
};

const MedicineThumb = ({
	url,
	secondaryUrls,
	alt,
}: {
	url?: string | null;
	secondaryUrls?: (string | null)[] | null;
	alt: string;
}) => {
	const primary = medicineImageUrl(url);
	const secondary = (secondaryUrls || [])
		.map((u) => medicineImageUrl(u))
		.filter(Boolean) as string[];
	const all = primary ? [primary, ...secondary] : secondary;

	if (all.length === 0) {
		return (
			<div className={`${styles.thumb} ${styles.thumbPlaceholder}`}>
				No image
			</div>
		);
	}

	return (
		<Image.PreviewGroup items={all}>
			<Image
				className={styles.thumb}
				src={all[0]}
				alt={alt}
				width={56}
				height={56}
				style={{ objectFit: 'cover', borderRadius: 8 }}
			/>
		</Image.PreviewGroup>
	);
};

const MapMedicineModal = ({
	open,
	draftRequest,
	onClose,
	onMapped,
}: MapMedicineModalProps) => {
	const isAdmin =
		useSelector((state: any) => state?.userProfile?.data?.isAdmin) === true;
	const [search, setSearch] = useState('');
	const [targets, setTargets] = useState<MapTargetRow[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [selectedTarget, setSelectedTarget] = useState<MapTargetRow | null>(
		null,
	);
	const [isMapping, setIsMapping] = useState(false);
	const [draftDetails, setDraftDetails] =
		useState<MedicineDetailsByIdsRow | null>(null);
	const [isLoadingDraft, setIsLoadingDraft] = useState(false);

	const debouncedSearch = useDebounce(search, 400);

	const resetState = useCallback(() => {
		setSearch('');
		setTargets([]);
		setSelectedTarget(null);
		setIsSearching(false);
		setIsMapping(false);
		setDraftDetails(null);
		setIsLoadingDraft(false);
	}, []);

	useEffect(() => {
		if (!open) {
			resetState();
		}
	}, [open, resetState]);

	useEffect(() => {
		if (!open || !draftRequest?.medicineId) return;
		let cancelled = false;
		setIsLoadingDraft(true);
		(async () => {
			try {
				const detailsRes = await postApi(
					'/medicine/details-by-ids',
					{ medicineIds: [draftRequest.medicineId] },
					false,
				);
				if (cancelled) return;
				const medicine = (detailsRes?.data?.data?.[0] ||
					null) as MedicineDetailsByIdsRow | null;
				setDraftDetails(medicine);
			} catch (error: any) {
				if (cancelled) return;
				toastText(
					getAxiosErrorMessage(
						error,
						'Failed to load draft medicine details',
					),
					'error',
				);
				setDraftDetails(null);
			} finally {
				if (!cancelled) setIsLoadingDraft(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [open, draftRequest?.medicineId]);

	useEffect(() => {
		if (!open || selectedTarget) return;
		let cancelled = false;
		const term = debouncedSearch.trim();
		setIsSearching(true);
		(async () => {
			try {
				const res = await getMapTargets(
					term.length > 0 ? term : undefined,
				);
				if (cancelled) return;
				setTargets(res?.data?.data || []);
			} catch (error: any) {
				if (cancelled) return;
				toastText(
					getAxiosErrorMessage(
						error,
						'Failed to load target medicines',
					),
					'error',
				);
				setTargets([]);
			} finally {
				if (!cancelled) setIsSearching(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, open, selectedTarget]);

	const handleMap = async () => {
		if (!draftRequest || !selectedTarget) return;
		if (draftRequest.medicineId === selectedTarget.id) {
			toastText('A draft cannot be mapped onto itself', 'error');
			return;
		}
		setIsMapping(true);
		const fromId = draftRequest.medicineId;
		const toId = selectedTarget.id;
		try {
			await postMapDraftRequest(draftRequest.id, selectedTarget.id);

			try {
				await postMedicineCatalogCsvSync();
			} catch (syncError: any) {
				toastText(
					getAxiosErrorMessage(
						syncError,
						'Draft mapped, but catalog sync failed. Please run a sync.',
					),
					'error',
				);
			}

			let stockOk = false;
			try {
				const stockRes = await postAdminPanelRemapStock({
					fromId,
					toId,
				});
				if (pharmaciesFailedCount(stockRes) > 0) {
					toastText(
						'Draft mapped, but some pharmacies failed stock remap. Retry remap-stock then remap-transactions.',
						'error',
					);
				} else {
					stockOk = true;
				}
			} catch (stockError: any) {
				toastText(
					getAxiosErrorMessage(
						stockError,
						'Draft mapped, but pharmacy stock remap failed. Retry remap-stock then remap-transactions.',
					),
					'error',
				);
			}

			if (stockOk) {
				try {
					const txnRes = await postAdminPanelRemapTransactions({
						fromId,
						toId,
					});
					if (pharmaciesFailedCount(txnRes) > 0) {
						toastText(
							'Draft mapped and stock remapped, but some pharmacies failed bill/ledger remap. Retry remap-transactions.',
							'error',
						);
					}
				} catch (txnError: any) {
					toastText(
						getAxiosErrorMessage(
							txnError,
							'Draft mapped and stock remapped, but bill/ledger remap failed. Retry remap-transactions.',
						),
						'error',
					);
				}
			}

			toastText('Draft medicine mapped successfully', 'success');
			onMapped();
			onClose();
		} catch (error: any) {
			toastText(
				getAxiosErrorMessage(error, 'Failed to map draft medicine'),
				'error',
			);
		} finally {
			setIsMapping(false);
		}
	};

	const openConfirmMap = () => {
		if (!draftRequest || !selectedTarget) return;
		if (draftRequest.medicineId === selectedTarget.id) {
			toastText('A draft cannot be mapped onto itself', 'error');
			return;
		}

		const targetName = selectedTarget.medicineName;
		const sourceName =
			draftDetails?.medicineName || draftRequest.medicineName || 'this draft';

		Modal.confirm({
			title: 'Confirm mapping',
			content: `Map "${sourceName}" to "${targetName}"? This cannot be undone.`,
			okText: 'Yes',
			cancelText: 'No',
			centered: true,
			closable: true,
			okButtonProps: {
				style: { background: '#00b5b0', borderColor: '#00b5b0' },
			},
			footer: (_, { OkBtn, CancelBtn }) => (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 8,
					}}
				>
					<OkBtn />
					<CancelBtn />
				</div>
			),
			onOk: handleMap,
		});
	};

	const draftName =
		draftDetails?.medicineName || draftRequest?.medicineName || '—';
	const draftQuantity = formatQuantity(
		draftDetails?.numberOfItems,
		draftDetails?.unitOfMeasure,
	);
	const draftStatus = (draftRequest?.status || 'PENDING').toUpperCase();

	return (
		<Modal
			centered
			title={
				<div>
					<div>Map Draft Medicine</div>
					<div
						style={{
							fontSize: 13,
							fontWeight: 400,
							color: '#8c8c8c',
							marginTop: 4,
						}}
					>
						Map this draft medicine to an existing approved
						medicine.
					</div>
				</div>
			}
			open={open}
			onCancel={isMapping ? undefined : onClose}
			onOk={openConfirmMap}
			okText="Map Medicine"
			okButtonProps={{
				disabled: !selectedTarget,
				loading: isMapping,
				style: selectedTarget
					? { background: '#00b5b0', borderColor: '#00b5b0' }
					: undefined,
			}}
			cancelButtonProps={{ disabled: isMapping }}
			maskClosable={false}
			destroyOnClose
			width={720}
		>
			<div className={styles.modalBody}>
				<section>
					<p className={styles.sectionLabel}>Draft Medicine</p>
					{isLoadingDraft ? (
						<div className={styles.loadingState}>
							<Spin />
						</div>
					) : (
						<div className={styles.card}>
							<div className={styles.cardHeader}>
								<MedicineThumb
									url={draftDetails?.medicinePrimaryImageUrl}
									secondaryUrls={
										draftDetails?.medicineSecondaryImageUrl
									}
									alt={draftName}
								/>
								<div className={styles.cardTitleBlock}>
									<p className={styles.cardTitle}>
										{draftName}
									</p>
									{draftQuantity && (
										<p className={styles.cardSubtitle}>
											{draftQuantity}
										</p>
									)}
								</div>
								<span
									className={`${styles.badge} ${styles.badgePending}`}
								>
									<span className={styles.badgeDot} />
									{formatLabel(draftStatus) || 'Pending'}
								</span>
							</div>
							<div className={styles.metaGrid}>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>
										Pharmacy Code
									</span>
									<TruncatedMetaValue
										value={draftRequest?.pharmacyCode}
									/>
								</div>
								{isAdmin && (
									<div className={styles.metaItem}>
										<span className={styles.metaLabel}>
											Pharmacy Name
										</span>
										<TruncatedMetaValue
											value={draftRequest?.pharmacyName}
										/>
									</div>
								)}
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>
										Marketed By
									</span>
									<TruncatedMetaValue value={draftDetails?.marketedBy} />
								</div>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>
										Weightage
									</span>
									<span className={styles.metaValue}>
										{displayOrDash(
											formatProductSize(
												draftDetails?.weightage,
												draftDetails?.unitOfMeasure,
											),
										)}
									</span>
								</div>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>MRP</span>
									<span className={styles.metaValue}>
										{displayOrDash(formatMrp(draftDetails?.mrp))}
									</span>
								</div>
							</div>
						</div>
					)}
				</section>

				<p className={styles.sectionCenterLabel}>Target Medicine</p>

				{selectedTarget ? (
					<section>
						<div className={styles.sectionHeader}>
							<p
								className={styles.sectionLabel}
								style={{ marginBottom: 0 }}
							>
								Selected Medicine
							</p>
							<button
								type="button"
								className={styles.replaceBtn}
								onClick={() => setSelectedTarget(null)}
								disabled={isMapping}
							>
								<ReloadOutlined />
								Replace
							</button>
						</div>
						<div
							className={`${styles.card} ${styles.cardSelected}`}
						>
							<div className={styles.cardHeader}>
								<MedicineThumb
									url={selectedTarget.medicinePrimaryImageUrl}
									secondaryUrls={
										selectedTarget.medicineSecondaryImageUrl
									}
									alt={selectedTarget.medicineName}
								/>
								<div className={styles.cardTitleBlock}>
									<p className={styles.cardTitle}>
										{selectedTarget.medicineName}
									</p>
									{formatQuantity(
										selectedTarget.numberOfItems,
										selectedTarget.unitOfMeasure,
									) && (
										<p className={styles.cardSubtitle}>
											{formatQuantity(
												selectedTarget.numberOfItems,
												selectedTarget.unitOfMeasure,
											)}
										</p>
									)}
								</div>
								<span
									className={`${styles.badge} ${styles.badgeApproved}`}
								>
									Approved
								</span>
							</div>
							<div
								className={`${styles.metaGrid} ${styles.metaGridInline}`}
							>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>
										Marketed By
									</span>
									<TruncatedMetaValue value={selectedTarget.marketedBy} />
								</div>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>
										Weightage
									</span>
									<span className={styles.metaValue}>
										{displayOrDash(
											formatProductSize(
												selectedTarget.weightage,
												selectedTarget.unitOfMeasure,
											),
										)}
									</span>
								</div>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>
										Dosage Form
									</span>
									<span className={styles.metaValue}>
										{displayOrDash(
											formatLabel(
												selectedTarget.unitOfMeasure,
											) || selectedTarget.unitOfMeasure,
										)}
									</span>
								</div>
								<div className={styles.metaItem}>
									<span className={styles.metaLabel}>MRP</span>
									<span className={styles.metaValue}>
										{displayOrDash(formatMrp(selectedTarget.mrp))}
									</span>
								</div>
							</div>
						</div>
					</section>
				) : (
					<section>
						<p className={styles.sectionLabel}>
							Search Approved Medicines
						</p>
						<div className={styles.searchWrap}>
							<Input
								size="large"
								allowClear
								prefix={
									<SearchOutlined
										style={{ color: '#8c8c8c' }}
									/>
								}
								placeholder="Search approved medicines by name, ID, manufacturer..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								disabled={isMapping}
							/>
						</div>
						<div className={styles.results}>
							{isSearching ? (
								<div className={styles.loadingState}>
									<Spin size="small" />
								</div>
							) : targets.length === 0 ? (
								<div className={styles.emptyState}>
									No approved medicines found
								</div>
							) : (
								targets.map((target) => {
									const tags = getTargetLabeledTags(target);

									return (
										<div
											key={target.id}
											className={styles.resultCard}
										>
											<MedicineThumb
												url={
													target.medicinePrimaryImageUrl
												}
												secondaryUrls={
													target.medicineSecondaryImageUrl
												}
												alt={target.medicineName}
											/>
											<div className={styles.resultBody}>
												<p
													className={
														styles.resultTitle
													}
												>
													{target.medicineName}
												</p>
												<div className={styles.tags}>
													{tags.map((tag) => (
														<LabeledTagChip
															key={`${target.id}-${tag.key}`}
															tag={tag}
														/>
													))}
												</div>
											</div>
											<Button
												type="primary"
												className={styles.selectBtn}
												style={{
													background: '#00b5b0',
													borderColor: '#00b5b0',
												}}
												onClick={() =>
													setSelectedTarget(target)
												}
												disabled={isMapping}
											>
												Select
											</Button>
										</div>
									);
								})
							)}
						</div>
					</section>
				)}

				<Alert
					type="warning"
					showIcon
					message="This action cannot be undone."
					description="All pending requests for this draft will be closed as MAPPED, the draft will be retired from the catalog, and pharmacy stock plus live bills/returns/ledger rows will be remapped to the target medicine."
				/>
			</div>
		</Modal>
	);
};

export default MapMedicineModal;
